import React, { useEffect, useState } from "react";
import { Save, Loader2, Plus, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { TextField, Section, StringList, KeyValueList, PageHeader } from "./AdminUI";
import ImageUploader from "./ImageUploader";
import PdfUploader from "./PdfUploader";

const SiteAdmin = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const site = await api.getSite();
        setData(site || {});
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (path, value) => {
    setData((d) => {
      const next = { ...(d || {}) };
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        cur[parts[i]] = { ...(cur[parts[i]] || {}) };
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const saved = await api.saveSite(data);
      setData(saved);
      toast({ title: "Saved", description: "Site info updated. Live site reflects it instantly." });
    } catch (err) {
      toast({ title: "Couldn't save", description: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="py-20 text-center">
        <Loader2 className="w-6 h-6 animate-spin inline-block text-[color:var(--muted)]" />
      </div>
    );
  }

  return (
    <form onSubmit={onSave} className="space-y-8">
      <PageHeader
        label="Site Info"
        title="Brand details & site copy"
        action={
          <button type="submit" disabled={saving} className="mk-btn mk-btn-primary disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
          </button>
        }
      />

      <Section title="Identity">
        <div className="grid md:grid-cols-2 gap-5">
          <TextField label="Full name" value={data.name} onChange={(v) => set("name", v)} />
          <TextField label="Role / title" value={data.role} onChange={(v) => set("role", v)} />
        </div>
        <TextField label="Tagline" value={data.tagline} onChange={(v) => set("tagline", v)} />
        <div className="grid md:grid-cols-3 gap-5">
          <TextField label="Location" value={data.location} onChange={(v) => set("location", v)} />
          <TextField label="Email" type="email" value={data.email} onChange={(v) => set("email", v)} />
          <TextField label="Phone" value={data.phone} onChange={(v) => set("phone", v)} />
        </div>
      </Section>

      <Section title="Social links">
        <div className="grid md:grid-cols-2 gap-5">
          <TextField label="Instagram URL" value={data.socials?.instagram} onChange={(v) => set("socials.instagram", v)} />
          <TextField label="Behance URL" value={data.socials?.behance} onChange={(v) => set("socials.behance", v)} />
          <TextField label="Dribbble URL" value={data.socials?.dribbble} onChange={(v) => set("socials.dribbble", v)} />
          <TextField label="LinkedIn URL" value={data.socials?.linkedin} onChange={(v) => set("socials.linkedin", v)} />
        </div>
      </Section>

      <Section title="Hero section" hint="Top of homepage">
        <TextField label="Availability badge" value={data.hero?.badge_text} onChange={(v) => set("hero.badge_text", v)} />
        <div className="grid md:grid-cols-3 gap-5">
          <TextField label="Heading prefix" value={data.hero?.heading_pre} onChange={(v) => set("hero.heading_pre", v)} placeholder="Hi, I'm" />
          <TextField label="Highlighted word" value={data.hero?.heading_em} onChange={(v) => set("hero.heading_em", v)} placeholder="graphic" />
          <TextField label="Heading suffix" value={data.hero?.heading_post} onChange={(v) => set("hero.heading_post", v)} placeholder="designer for playful brands." />
        </div>
        <TextField label="Body copy" textarea rows={3} value={data.hero?.body} onChange={(v) => set("hero.body", v)} />
        <div className="grid md:grid-cols-2 gap-5">
          <TextField label="Primary CTA" value={data.hero?.primary_cta} onChange={(v) => set("hero.primary_cta", v)} />
          <TextField label="Secondary CTA" value={data.hero?.secondary_cta} onChange={(v) => set("hero.secondary_cta", v)} />
        </div>
        <TextField label="Rating text (under stats)" value={data.hero?.rating_text} onChange={(v) => set("hero.rating_text", v)} />
        <ImageUploader
          label="Hero photo (shown on right side of landing page)"
          value={data.hero?.image}
          onChange={(v) => set("hero.image", v)}
          aspect="aspect-[4/5]"
        />
      </Section>

      <Section title="Stats" hint="Numbers displayed in hero">
        <KeyValueList
          items={data.stats || []}
          onChange={(v) => set("stats", v)}
          keyName="value"
          valueName="label"
          keyPlaceholder="e.g. 120+"
          valuePlaceholder="e.g. Projects delivered"
        />
      </Section>

      <Section title="Marquee words" hint="Animated keywords strip">
        <StringList items={data.marquee_words || []} onChange={(v) => set("marquee_words", v)} placeholder="e.g. Brand Identity" />
      </Section>

      <Section title="Footer">
        <div className="grid md:grid-cols-3 gap-5">
          <TextField label="Footer prefix" value={data.footer?.tagline_pre} onChange={(v) => set("footer.tagline_pre", v)} />
          <TextField label="Highlighted word" value={data.footer?.tagline_em} onChange={(v) => set("footer.tagline_em", v)} />
          <TextField label="Suffix" value={data.footer?.tagline_post} onChange={(v) => set("footer.tagline_post", v)} />
        </div>
        <TextField label="Footer CTA text" value={data.footer?.cta_text} onChange={(v) => set("footer.cta_text", v)} />
      </Section>

      <Section title="Contact form options">
        <TextField label="Contact intro paragraph" textarea rows={3} value={data.contact_intro} onChange={(v) => set("contact_intro", v)} />
        <div className="grid md:grid-cols-2 gap-6">
          <StringList label="Service options" items={data.contact_options?.services || []} onChange={(v) => set("contact_options.services", v)} placeholder="e.g. Social Media" />
          <StringList label="Budget options" items={data.contact_options?.budgets || []} onChange={(v) => set("contact_options.budgets", v)} placeholder="e.g. $1k – $3k" />
        </div>
      </Section>

      <Section title="Resume" hint="Uploaded PDF appears as a 'Resume' button in the top nav.">
        <ResumeUploader value={data.resume_url} onChange={(v) => set("resume_url", v)} />
      </Section>

      <Section title="Brands strip" hint="Logos shown below the hero. Leave logo URL empty to show the brand name as text.">
        <BrandsEditor items={data.brands || []} onChange={(v) => set("brands", v)} />
      </Section>

      <Section title="Software &amp; Tools cards" hint="The 3 toolkit cards. Icon key = design | interactive | ai_building. All cards use the purple tint.">
        <TextField label="Section heading" value={data.stack_heading} onChange={(v) => set("stack_heading", v)} placeholder="My toolkit" />
        <TextField label="Section subheading" textarea rows={2} value={data.stack_subheading} onChange={(v) => set("stack_subheading", v)} placeholder="Short intro shown under the heading." />
        <StackEditor items={data.stack || []} onChange={(v) => set("stack", v)} />
      </Section>

      <Section title="Areas of Expertise" hint="Overlapping fanned banners. Colors are hex (e.g. #CDE8CF).">
        <ExpertiseEditor items={data.expertise_areas || []} onChange={(v) => set("expertise_areas", v)} />
      </Section>

      <Section title="Latest Design Shots" hint="Horizontal carousel of mockup/design cards. Optional.">
        <DesignShotsEditor items={data.design_shots || []} onChange={(v) => set("design_shots", v)} />
      </Section>

      <Section title="Chat contact section" hint="The 'Let's have coffee?' chat mockup on the landing page.">
        <div className="grid md:grid-cols-2 gap-5">
          <TextField label="Eyebrow" value={data.chat_contact?.eyebrow} onChange={(v) => set("chat_contact.eyebrow", v)} placeholder="Contact" />
          <TextField label="Heading" value={data.chat_contact?.heading} onChange={(v) => set("chat_contact.heading", v)} placeholder="Let's have coffee?" />
        </div>
        <TextField label="Subtext" textarea rows={2} value={data.chat_contact?.subtext} onChange={(v) => set("chat_contact.subtext", v)} />
        <TextField label="Chat question (from Misbah)" value={data.chat_contact?.question} onChange={(v) => set("chat_contact.question", v)} placeholder="Want to work together?" />
        <TextField label="Reply prefix (before email)" value={data.chat_contact?.reply_prefix} onChange={(v) => set("chat_contact.reply_prefix", v)} placeholder="Email me at:" />
      </Section>

      <div className="flex justify-end">
        <button type="submit" disabled={saving} className="mk-btn mk-btn-primary disabled:opacity-60">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
};

/* ---------- Sub-editors ---------- */

const ResumeUploader = ({ value, onChange }) => (
  <div>
    {value ? (
      <div className="flex items-center gap-3 p-3 rounded-xl bg-white border border-black/10">
        <span className="text-xs px-2 py-1 rounded-full bg-[color:var(--coral-soft)]">PDF</span>
        <a
          href={value.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${value}` : value}
          target="_blank"
          rel="noreferrer"
          className="flex-1 min-w-0 text-sm truncate underline underline-offset-2"
        >
          {value}
        </a>
        <button type="button" onClick={() => onChange("")} className="text-xs text-[color:var(--coral)] hover:underline">Remove</button>
      </div>
    ) : (
      <div className="text-sm text-[color:var(--muted)]">No resume uploaded yet.</div>
    )}
    <div className="mt-3">
      <PdfUploader
        label=""
        items={value ? [{ name: "Resume", url: value }] : []}
        onChange={(list) => onChange(list.length ? list[list.length - 1].url : "")}
      />
    </div>
  </div>
);

const BrandsEditor = ({ items = [], onChange }) => {
  const update = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, { name: "", logo_url: "" }]);
  return (
    <div>
      <div className="space-y-3">
        {items.map((b, i) => (
          <div key={i} className="p-3 rounded-xl bg-white border border-black/10">
            <div className="grid md:grid-cols-2 gap-3">
              <TextField label="Brand name" value={b.name} onChange={(v) => update(i, { name: v })} placeholder="Beiersdorf" />
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-[color:var(--muted)]">Logo (optional)</div>
                <ImageUploader label="" value={b.logo_url} onChange={(v) => update(i, { logo_url: v })} aspect="aspect-[3/1]" />
              </div>
            </div>
            <button type="button" onClick={() => remove(i)} className="mt-2 text-xs text-[color:var(--coral)] hover:underline inline-flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="mt-3 inline-flex items-center gap-1.5 text-sm text-[color:var(--coral)] hover:underline">
        <Plus className="w-4 h-4" /> Add brand
      </button>
    </div>
  );
};

const ICON_KEYS = [
  { key: "design", label: "Design (pink diamond)" },
  { key: "interactive", label: "Interactive & E-learning (tablet)" },
  { key: "ai_building", label: "AI Building (magic wand)" },
];
const TONE_KEYS = ["lilac"];

const StackEditor = ({ items = [], onChange }) => {
  const update = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, { title: "", subtitle: "", icon_key: "design", tone: "lilac", tools: [] }]);
  return (
    <div>
      <div className="space-y-4">
        {items.map((c, i) => (
          <div key={i} className="p-4 rounded-xl bg-white border border-black/10">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wider text-[color:var(--muted)]">Card {i + 1}</span>
              <button type="button" onClick={() => remove(i)} className="text-xs text-[color:var(--coral)] hover:underline inline-flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <TextField label="Title" value={c.title} onChange={(v) => update(i, { title: v })} placeholder="Design & Illustration" />
              <TextField label="Subtitle" value={c.subtitle} onChange={(v) => update(i, { subtitle: v })} placeholder="Where every project begins." />
            </div>
            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-[color:var(--muted)] mb-2">Icon</div>
                <select value={c.icon_key || "design"} onChange={(e) => update(i, { icon_key: e.target.value })} className="w-full bg-transparent border border-black/15 rounded-lg px-3 py-2 text-sm">
                  {ICON_KEYS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-[color:var(--muted)] mb-2">Tint</div>
                <select value={c.tone || "lilac"} onChange={(e) => update(i, { tone: e.target.value })} className="w-full bg-transparent border border-black/15 rounded-lg px-3 py-2 text-sm">
                  {TONE_KEYS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-4">
              <StringList label="Tools" items={c.tools || []} onChange={(v) => update(i, { tools: v })} placeholder="e.g. Figma" />
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="mt-3 inline-flex items-center gap-1.5 text-sm text-[color:var(--coral)] hover:underline">
        <Plus className="w-4 h-4" /> Add tool card
      </button>
    </div>
  );
};

const EXPERTISE_PRESETS = ["#CDE8CF", "#FFD9C7", "#D9E0FF", "#E1D8FF", "#FFE6A7"];
const ExpertiseEditor = ({ items = [], onChange }) => {
  const update = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, { title: "", description: "", color: "#E1D8FF" }]);
  return (
    <div>
      <div className="space-y-3">
        {items.map((a, i) => (
          <div key={i} className="p-4 rounded-xl bg-white border border-black/10">
            <TextField label="Title" value={a.title} onChange={(v) => update(i, { title: v })} placeholder="Strategy & Research" />
            <TextField label="Description" textarea rows={2} value={a.description} onChange={(v) => update(i, { description: v })} placeholder="Short one-liner." />
            <div className="mt-3">
              <div className="text-xs font-medium uppercase tracking-wider text-[color:var(--muted)] mb-2">Background color</div>
              <div className="flex flex-wrap gap-2 items-center">
                {EXPERTISE_PRESETS.map((c) => (
                  <button key={c} type="button" onClick={() => update(i, { color: c })} className={`w-8 h-8 rounded-full border-2 ${a.color === c ? "border-[color:var(--ink)] scale-110" : "border-transparent"}`} style={{ backgroundColor: c }} aria-label={c} />
                ))}
                <input type="text" value={a.color || ""} onChange={(e) => update(i, { color: e.target.value })} className="ml-2 w-32 bg-transparent border border-black/15 rounded-md px-2 py-1 text-sm" placeholder="#E1D8FF" />
              </div>
            </div>
            <button type="button" onClick={() => remove(i)} className="mt-3 text-xs text-[color:var(--coral)] hover:underline inline-flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="mt-3 inline-flex items-center gap-1.5 text-sm text-[color:var(--coral)] hover:underline">
        <Plus className="w-4 h-4" /> Add area
      </button>
    </div>
  );
};

const DesignShotsEditor = ({ items = [], onChange }) => {
  const update = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, { image: "", title: "", caption: "" }]);
  return (
    <div>
      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((s, i) => (
          <div key={i} className="p-3 rounded-xl bg-white border border-black/10 space-y-2">
            <ImageUploader label={`Shot ${i + 1}`} value={s.image} onChange={(v) => update(i, { image: v })} aspect="aspect-[3/4]" />
            <TextField label="Title" value={s.title} onChange={(v) => update(i, { title: v })} placeholder="Sunset UI" />
            <TextField label="Caption" value={s.caption} onChange={(v) => update(i, { caption: v })} placeholder="Short note." />
            <button type="button" onClick={() => remove(i)} className="text-xs text-[color:var(--coral)] hover:underline inline-flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} className="mt-3 inline-flex items-center gap-1.5 text-sm text-[color:var(--coral)] hover:underline">
        <Plus className="w-4 h-4" /> Add shot
      </button>
    </div>
  );
};

export default SiteAdmin;
